
import Vue from 'vue';
import { Modal } from '@descomplica/vue-components';
import NameCheckingModal from './partials/NameCheckingModal.vue';
import LinkedinModal from './partials/LinkedinModal.vue';
import LoadingModal from './partials/LoadingModal.vue';
import SharingAndDownloadModal from './partials/SharingAndDownloadModal.vue';
import SharedInLinkedinModal from './partials/SharedInLinkedinModal.vue';
import { ClassroomModel, CourseModel, UserModel } from '@descomplica/dex-models';
import MicrocertificateRequestsService, { Urls } from '@/helpers/postgradMicrocertificate/request';
import { getDexToken } from '@/helpers/get-tokens';
import { LocaleMessage } from 'vue-i18n';
import Analytics from '@/helpers/analytics';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import { getCourseCategory } from '@/helpers/category';
import { listenLinkedinPopup, openLinkedinPopup } from '@/helpers/linkedin';
import { PostgradEarlyGraduationTrackingInformation } from '@/models/postgradEarlyGraduation/PostgradEarlyGraduationModel';
import { ChimeraCourseModel } from '@/models/chimera/ChimeraCourseModel';

interface Data {
  step: number;
  fetchingMicrocertificateUrls: boolean;
  microcertificateUrls: Urls;
  shareStatus: boolean;
  firstLoadingMessages: { messages: LocaleMessage; subtitle: LocaleMessage };
  linkedinOAuthCode: string;
  linkedinPopup: Window | null;
}

interface BaseTrackers {
  course: string;
  category: string | null;
  discipline: string;
}

export default Vue.extend({
  components: {
    Modal,
    NameCheckingModal,
    LinkedinModal,
    LoadingModal,
    SharingAndDownloadModal,
    SharedInLinkedinModal,
  },
  props: {
    showMicrocertificateDownloadModal: {
      type: Boolean,
      default: false,
    },
    classroom: {
      required: true,
      type: Object as Vue.PropType<ClassroomModel>,
    },
  },
  data(): Data {
    return {
      step: 1,
      fetchingMicrocertificateUrls: false,
      microcertificateUrls: {
        pdfUrl: '',
        imageUrl: '',
      },
      firstLoadingMessages: {
        messages: [
          this.$t('postgradMicrocertificate.loadingModal.summingMinutes'),
          this.$t('postgradMicrocertificate.loadingModal.checkingClasses'),
          this.$t('postgradMicrocertificate.loadingModal.preparing'),
        ],
        subtitle: this.$t('postgradMicrocertificate.loadingModal.creatingMicrocetificate'),
      },
      shareStatus: false,
      linkedinPopup: null,
      linkedinOAuthCode: '',
    };
  },
  computed: {
    hasMicrocertificateUrls(): boolean {
      return !!this.microcertificateUrls.pdfUrl && !!this.microcertificateUrls.imageUrl;
    },
    disciplineName(): string {
      return this.classroom.name;
    },
    currentUser(): UserModel {
      return this.$store.getters.currentUser;
    },
    currentCourse(): CourseModel {
      return this.$store.getters.currentCourse;
    },
    disciplineWorkload(): number {
      const boardsClassroomIds = this.classroom.boardsClassroomsByClassroomId;

      if (boardsClassroomIds.length !== 1) {
        // in postgrad context, a classroom should always have only one board
        return 0;
      }
      const boardClassroom = this.$store.getters.boardsClassroomById(boardsClassroomIds[0]);
      if (!boardClassroom) {
        return 0;
      }

      return boardClassroom.board?.workload;
    },
    linkedinPopupEventListener(): (this: Window, ev: MessageEvent<any>) => any {
      return (messageEvent: MessageEvent) =>
        listenLinkedinPopup({
          messageEvent,
          callback: this.setLinkedinOAuthCode,
          linkedinPopup: this.linkedinPopup!,
        });
    },
    currentChimeraCourse(): ChimeraCourseModel | null {
      return this.$store.getters.getCurrentChimeraCourse;
    },
    earlyGraduationTrackingProps(): PostgradEarlyGraduationTrackingInformation {
      return this.$store.getters.earlyGraduationTrackings(this.currentChimeraCourse);
    },
  },
  beforeDestroy() {
    window.removeEventListener('message', this.linkedinPopupEventListener);
  },
  methods: {
    async generateAndChangeStep(): Promise<void> {
      this.trackGenerateMicrocertificate();

      this.fetchingMicrocertificateUrls = true;
      const urls = await this.generateMicrocertificateFiles();
      if (!urls) {
        this.closeMicrocertificateDownloadModal();
        return;
      }
      this.microcertificateUrls = urls;
      this.fetchingMicrocertificateUrls = false;
      this.changeStep();
    },
    changeStep(): void {
      this.step += 1;
    },
    async generateMicrocertificateFiles(): Promise<Urls | null> {
      const user = this.currentUser;
      if (!user.id) return null;

      const course = this.currentCourse;
      const token = getDexToken();
      if (!course || !token) return null;

      const microcertificateUrls = await MicrocertificateRequestsService.getMicrocertificateUrl({
        user,
        course,
        classroom: this.classroom,
        token,
      });

      return microcertificateUrls;
    },
    closeMicrocertificateDownloadModal(): void {
      this.trackCloseModalInteraction();

      this.step = 1;
      this.$emit('onClose');
    },
    shareMicrocertificate(shareStatus: boolean): void {
      this.shareStatus = shareStatus;
      this.changeStep();
    },
    trackGenerateMicrocertificate(): void {
      Analytics.trackEvent(EVENT_LABELS.buttonClicked, {
        postgrad_microcertificate_name_checking_modal: true,
        postgrad_microcertificate_name_checking_modal_action: 'download',
        ...this.defaultTrackingProps(),
        ...this.earlyGraduationTrackingProps,
      });
    },
    trackCloseModalInteraction(): void {
      switch (this.step) {
        case 1:
          if (this.fetchingMicrocertificateUrls) {
            Analytics.trackEvent(EVENT_LABELS.modalClosed, {
              postgrad_microcertificate_loading_modal: true,
              ...this.defaultTrackingProps(),
            });
            break;
          } else {
            Analytics.trackEvent(EVENT_LABELS.modalClosed, {
              postgrad_microcertificate_name_checking_modal: true,
              ...this.defaultTrackingProps(),
            });
            break;
          }
        case 2:
          Analytics.trackEvent(EVENT_LABELS.modalClosed, {
            postgrad_microcertificate_sharing_and_download_modal: true,
            ...this.defaultTrackingProps(),
          });
          break;
        case 3:
          Analytics.trackEvent(EVENT_LABELS.modalClosed, {
            postgrad_microcertificate_linkedin_modal: true,
            ...this.defaultTrackingProps(),
          });
          break;
        case 4:
          Analytics.trackEvent(EVENT_LABELS.modalClosed, {
            ...this.defaultTrackingProps(),
            postgrad_microcertificate_shared_in_linkedin_modal: true,
            shared_successfully: this.shareStatus,
          });
          break;
        default:
      }
    },
    defaultTrackingProps(): BaseTrackers {
      return {
        course: this.currentCourse.name,
        category: getCourseCategory(this.currentCourse),
        discipline: this.disciplineName,
      };
    },
    openLinkedinPopup(): void {
      this.linkedinPopup = openLinkedinPopup();

      window.addEventListener('message', this.linkedinPopupEventListener, false);
    },
    setLinkedinOAuthCode(code: string) {
      this.linkedinOAuthCode = code;
      this.changeStep();
    },
  },
});
